import { bcApi } from "../services/apiClients";
import { formatAxiosError } from "./common";
import { getActiveEnvironment } from "../services/environmentService";
import { encodeBcCompanyName } from "./stringNormalization";
import enviroment from "../config/env";

export const getCurrentEnvironment = async () => {
	try {
		const activeEnv = await getActiveEnvironment();
		return { ...activeEnv, "bcBaseUrl": enviroment.bcBaseUrl } || enviroment;
	} catch (error) {
		console.warn("No se pudo obtener configuración activa, usando predeterminada:", error);
		return enviroment;
	}
};

// No es la forma correcta de manejarlo, en un mundo ideal si BC rompe devuelve un código distinto a 200
// Si falla Business Central a veces devuelve 200 y el flujo sigue normalmente! No permitiendome manejar el error como se debe
// Esta función es el último bastión para chequear si da error y permitirnos manejarlo
export const hasError = (value) => {
	if (typeof value === "string" && value.toLowerCase().startsWith("error:")) {
		return true;
	}
	return false;
};

const checkValueForErrors = (value) => {
	if (typeof value === "string" && value.toLowerCase().startsWith("error:")) {
		const errorMessage = value.substring(6).trim();
		console.error("Error de Business Central:", errorMessage);
		//alert(`Error en Business Central: ${errorMessage}`);
		return true;
	}
	return false;
};

// --- Fetch helpers ---

export const fetchFromBusinessCentral = async (endpoint, filter, isApiV2 = false) => {
	try {
		const env = await getCurrentEnvironment();
		const encodedCompany = encodeBcCompanyName(env.company);
		const base = isApiV2 ? `${env.bcBaseUrl}/${env.env}/api/excelia/automate/v1.0/companies(${enviroment.companyId})` :
							   `${env.bcBaseUrl}/${env.env}/ODataV4/Company('${encodedCompany}')`;
		const url = `${base}/${endpoint}${filter ? `?$filter=${filter}` : ''}`;
		const response = await bcApi.get(url);
		const value = response.data.value;

		if (checkValueForErrors(value)) return "";
		return value || [];
	} catch (error) {
		console.error(`Error al obtener datos de Business Central (${endpoint}):`, error);
		throw error;
	}
};

export const fetchFromBusinessCentralEspecial = async (endpoint, data = {}) => {
	try {
		const env = await getCurrentEnvironment();
		const encodedCompany = encodeBcCompanyName(env.company);
		const url = `${env.bcBaseUrl}/${env.env}/ODataV4/${endpoint}?company='${encodedCompany}'`;

		const response = await bcApi.post(url, data);
		const value = response.data.value;

		if (checkValueForErrors(value)) return "";
		return value || [];
	} catch (error) {
		console.error(`Error al obtener datos de Business Central (${endpoint}):`, error);
		throw error;
	}
};

export const sendToBusinessCentral = async (endpoint, data, isCustomEndpoint = false) => {
	try {
		const env = await getCurrentEnvironment();
		const encodedCompany = encodeBcCompanyName(env.company);
		const BC_BASE_URL = `${env.bcBaseUrl}/${env.env}/ODataV4`;

		const url = isCustomEndpoint
			? `${BC_BASE_URL}/${endpoint}?company='${encodedCompany}'`
			: `${BC_BASE_URL}/Company('${encodedCompany}')/${endpoint}`;

		const response = await bcApi.post(url, data);
		const responseData = response.data;

		if (responseData.value && checkValueForErrors(responseData.value)) {
			return { ...responseData, value: "" };
		}

		return responseData;
	} catch (error) {
		console.error(`Error al enviar datos a Business Central (${endpoint}):`, error);
		throw error;
	}
};

export const fetchInvoiceHistory = async (filter) => {
	try {
		const env = await getCurrentEnvironment();
		const encodedCompany = encodeBcCompanyName(env.company);

		const url = `${env.bcBaseUrl}/${env.env}/ODataV4/Company('${encodedCompany}')/salesInvoice?${filter}`;

		const response = await bcApi.get(url);
		return response.data?.value || [];
	} catch (error) {
		console.error(`Error al obtener facturas históricas:`, error);
		throw "No se pudieron obtener las facturas históricas. Inténtelo nuevamente más tarde.";
	}
};

//Metodo para contabilizar una factura en el momento de sincronizar ( Paraguay y Honduras)
export const postInvoice = async (systemId, isCreditMemo) => {
	try {
		const env = await getCurrentEnvironment();

		const baseUrl = `${env.bcBaseUrl}/${env.env}/api/v2.0/companies(${enviroment.companyId})`;
		const postInvoiceUrl = baseUrl + `/salesInvoices(${systemId})/Microsoft.NAV.post`;
		const postCreditMemoUrl = baseUrl + `/salesCreditMemos(${systemId})/Microsoft.NAV.postAndSend`;

		const url = isCreditMemo ? postCreditMemoUrl : postInvoiceUrl;

		const response = await bcApi.post(url);
		if (response.status == 204) { return true; }
		else {
			let message = response.data?.error?.message ||
				response.data?.error?.code ||
				`No se pudo contabilizar la factura. ${response?.message}`;
			window.messageApi.error(message,
				{
					modal: true,
					title: "Error Business Central"
				});
			return false;
		}
	} catch (error) {
		let message = formatAxiosError(error, `No se pudo contabilizar la factura. ${error?.message}`);

		window.messageApi.error(message, {
			modal: true,
			title: "Error Business Central"
		});
		return false;
	}
};

export const fetchFromApiV2 = async (endpoint, filter) => {
	try {
		const env = await getCurrentEnvironment();
		const base = `${env.bcBaseUrl}/${env.env}/api/v2.0/companies(${enviroment.companyId})`;
		const url = `${base}/${endpoint}${filter ? `?$filter=${filter}` : ''}`;
		const response = await bcApi.get(url);
		const value = response.data.value;

		if (checkValueForErrors(value)) return "";
		return value || [];
	} catch (error) {
		console.error(`Error al obtener datos de Business Central (${endpoint}):`, error);
		throw error;
	}
};

export const downloadInvoicePdfFromBC = async ({
	documentNo,
	documentType = "Invoice",
	fileName = "Factura.pdf",
}) => {
	try {
		const env = await getCurrentEnvironment();
		if (!documentNo) {
			window.messageApi.error("No se encontró número de documento para descargar el PDF.");
			return false;
		}

		const isCreditMemo = documentType === "Credit Memo";
		const endpoint = isCreditMemo
			? "PDFMgt_GetCrMemoPdfBase64"
			: "PDFMgt_GetInvoicePdfBase64";
		const companyByName = `company='${encodeBcCompanyName(env.company)}'`;
		const url = `${env.bcBaseUrl}/${env.env}/ODataV4/${endpoint}?${companyByName}`;

		const response = await bcApi.post(url, { input: documentNo });
		const rawValue = response?.data?.value;
		const pdfBase64 =
			typeof rawValue === "string"
				? rawValue.replace("data:application/pdf;base64,", "").trim()
				: "";

		if (!pdfBase64 || hasError(pdfBase64)) {
			throw new Error("Business Central no devolvió un PDF válido en base64.");
		}

		const binaryString = atob(pdfBase64);
		const byteNumbers = new Array(binaryString.length);
		for (let i = 0; i < binaryString.length; i++) {
			byteNumbers[i] = binaryString.codePointAt(i) || 0;
		}
		const byteArray = new Uint8Array(byteNumbers);
		const blob = new Blob([byteArray], { type: "application/pdf" });
		const downloadUrl = window.URL.createObjectURL(blob);

		const link = document.createElement("a");
		link.href = downloadUrl;
		link.download = fileName;
		document.body.appendChild(link);
		link.click();

		document.body.removeChild(link);
		window.URL.revokeObjectURL(downloadUrl);

		return true;
	} catch (error) {
		const message = formatAxiosError(
			error,
			"No se pudo descargar el PDF de la factura."
		);

		window.messageApi.error(message, {
			modal: true,
			title: "Error Business Central",
		});

		return false;
	}
};

